import { db } from '../firebase'
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
} from 'firebase/firestore'

// Interface for the raw Firestore student data (flat structure)
interface FirestoreStudentData {
  id: string
  studentId?: string
  name: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  instrument?: string
  instruments?: string[]
  grade?: number
  notes?: string
  avatar?: string
  profilePicture?: string
  profileImageUrl?: string
  linkedUid?: string
  status?: string
  lessonType?: 'In-person' | 'Online' | 'Hybrid'
  timeZone?: string
  // Flat address fields
  addressStreet?: string
  addressApt?: string
  addressCity?: string
  addressState?: string
  addressZip?: string
  // Flat guardian fields
  guardianName?: string
  guardianEmail?: string
  guardianPhone?: string
  guardianPreferredContact?: string
  guardianIsEmergencyContact?: boolean
  // Metadata
  createdAt?: string
  updatedAt?: string
  notesUpdatedAt?: string
}

// Interface for the transformed student data (nested structure)
export interface Student {
  id: string
  studentId?: string
  name: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  instrument?: string
  instruments?: string[]
  grade?: number
  notes?: string
  avatar?: string
  profilePicture?: string
  profileImageUrl?: string
  linkedUid?: string
  status?: string
  lessonType?: 'In-person' | 'Online' | 'Hybrid'
  timeZone?: string
  address?: {
    street?: string
    apt?: string
    city?: string
    state?: string
    zip?: string
  }
  guardian?: {
    name?: string
    email?: string
    phone?: string
    preferredContact?: string
    isEmergencyContact?: boolean
  }
  createdAt?: string
  updatedAt?: string
  notesUpdatedAt?: string
}

/**
 * Transform flat Firestore student data into nested object structure
 * This function converts flat fields like addressStreet, guardianName, etc.
 * into nested objects like address.street, guardian.name, etc.
 */
export function transformStudentData(
  firestoreData: FirestoreStudentData,
): Student {
  const student: Student = {
    id: firestoreData.id,
    studentId: firestoreData.studentId,
    name: firestoreData.name,
    firstName: firestoreData.firstName,
    lastName: firestoreData.lastName,
    email: firestoreData.email,
    phone: firestoreData.phone,
    instrument: firestoreData.instrument,
    instruments: firestoreData.instruments,
    grade: firestoreData.grade,
    notes: firestoreData.notes,
    avatar: firestoreData.avatar,
    profilePicture: firestoreData.profilePicture,
    profileImageUrl: firestoreData.profileImageUrl,
    linkedUid: firestoreData.linkedUid,
    status: firestoreData.status,
    lessonType: firestoreData.lessonType,
    timeZone: firestoreData.timeZone,
    createdAt: firestoreData.createdAt,
    updatedAt: firestoreData.updatedAt,
    notesUpdatedAt: firestoreData.notesUpdatedAt,
  }

  // Transform address fields into nested object
  if (
    firestoreData.addressStreet ||
    firestoreData.addressApt ||
    firestoreData.addressCity ||
    firestoreData.addressState ||
    firestoreData.addressZip
  ) {
    student.address = {
      street: firestoreData.addressStreet,
      apt: firestoreData.addressApt,
      city: firestoreData.addressCity,
      state: firestoreData.addressState,
      zip: firestoreData.addressZip,
    }
  }

  // Transform guardian fields into nested object
  if (
    firestoreData.guardianName ||
    firestoreData.guardianEmail ||
    firestoreData.guardianPhone ||
    firestoreData.guardianPreferredContact ||
    firestoreData.guardianIsEmergencyContact !== undefined
  ) {
    student.guardian = {
      name: firestoreData.guardianName,
      email: firestoreData.guardianEmail,
      phone: firestoreData.guardianPhone,
      preferredContact: firestoreData.guardianPreferredContact,
      isEmergencyContact: firestoreData.guardianIsEmergencyContact,
    }
  }

  return student
}

/**
 * Fire-and-forget backfill: For students with linkedUid but no profileImageUrl,
 * read the student's user doc and copy profileImageUrl to the teacher's student doc.
 * Updates the in-memory student objects so the current render shows the photo.
 */
function backfillStudentProfilePhotos(
  teacherId: string,
  students: Student[],
): void {
  const candidates = students.filter((s) => s.linkedUid && !s.profileImageUrl)

  if (candidates.length === 0) return

  console.log(
    `[Backfill] Found ${candidates.length} student(s) with linkedUid but no profileImageUrl`,
  )

  // Fire-and-forget — do not await
  Promise.all(
    candidates.map(async (student) => {
      try {
        const userDocRef = doc(db, 'users', student.linkedUid!)
        const userSnap = await getDoc(userDocRef)

        if (!userSnap.exists()) return

        const userData = userSnap.data()
        const photoUrl = userData?.profileImageUrl

        if (!photoUrl) return

        // Update in-memory object so current render picks it up
        student.profileImageUrl = photoUrl

        // Persist to teacher's student doc so future fetches don't need backfill
        const teacherStudentRef = doc(
          db,
          'teachers',
          teacherId,
          'students',
          student.id,
        )
        await updateDoc(teacherStudentRef, { profileImageUrl: photoUrl })

        console.log(
          `[Backfill] Synced profileImageUrl for student ${student.id} (${student.name})`,
        )
      } catch (err) {
        console.warn(
          `[Backfill] Failed to backfill photo for student ${student.id}:`,
          err,
        )
      }
    }),
  ).catch((err) => {
    console.warn('[Backfill] Unexpected error in backfill batch:', err)
  })
}

/**
 * Get all students for a teacher from Firestore
 * Automatically transforms flat Firestore data into nested object structure
 */
export async function getTeacherStudents(
  teacherId: string,
): Promise<Student[]> {
  try {
    console.log('getTeacherStudents: Fetching students for teacher:', teacherId)
    // Query the students subcollection under the teacher document
    const studentsRef = collection(db, 'teachers', teacherId, 'students')
    const studentsSnapshot = await getDocs(studentsRef)
    console.log('getTeacherStudents: Found', studentsSnapshot.size, 'students')

    // Transform each document into the nested structure
    const students: Student[] = studentsSnapshot.docs.map((doc) => {
      const data = doc.data() as FirestoreStudentData
      // Log raw data for debugging
      console.log('Raw Firestore data for student:', doc.id, {
        hasAddressStreet: !!data.addressStreet,
        hasGuardianName: !!data.guardianName,
        addressStreet: data.addressStreet,
        guardianName: data.guardianName,
      })

      // Transform the data
      const transformedStudent = transformStudentData({
        ...data,
        id: doc.id,
      })

      // Log transformed data for debugging
      console.log('Transformed student data:', doc.id, {
        hasAddress: !!transformedStudent.address,
        hasGuardian: !!transformedStudent.guardian,
        address: transformedStudent.address,
        guardian: transformedStudent.guardian,
      })

      return transformedStudent
    })

    console.log(
      'getTeacherStudents: Successfully transformed',
      students.length,
      'students',
    )

    // Fire-and-forget: backfill profile photos for linked students missing them
    backfillStudentProfilePhotos(teacherId, students)

    return students
  } catch (error) {
    console.error('getTeacherStudents: Error fetching students:', error)
    throw error
  }
}

/**
 * Get a single student by ID for a teacher
 * Automatically transforms flat Firestore data into nested object structure
 */
export async function getTeacherStudent(
  teacherId: string,
  studentId: string,
): Promise<Student | null> {
  try {
    console.log('getTeacherStudent: Fetching student:', {
      teacherId,
      studentId,
    })
    const students = await getTeacherStudents(teacherId)
    const student = students.find(
      (s) => s.id === studentId || s.studentId === studentId,
    )
    if (!student) {
      console.log('getTeacherStudent: Student not found')
      return null
    }
    console.log('getTeacherStudent: Found student with transformed data:', {
      hasAddress: !!student.address,
      hasGuardian: !!student.guardian,
    })
    return student
  } catch (error) {
    console.error('getTeacherStudent: Error fetching student:', error)
    throw error
  }
}
